<template>
  <div style="background-color:black; min-height: 100vh; padding: 16px;">
    <v-container>
      <v-row justify="center">
        <!-- Cards with Dynamic Data and Charts -->
        <v-col cols="12" md="6" lg="3" v-for="(item, index) in reportItems" :key="index">
          <v-card class="mx-auto modern-card" max-width="344" @click="showDialog(item)">
          <v-img :src="item.img" height="200px" class="modern-card-image">
            <v-row class="fill-height align-end justify-center">
              <span class="modern-card-number">{{ item.title }}</span>
            </v-row>
          </v-img>
          <v-card-text class="modern-card-details">
            <div v-for="(stat, idx) in item.statsInfo" :key="idx">
              <strong>{{ stat.label }}:</strong> {{ stat.value }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn width="100%" rounded dark color="#ff3b47" @click="showDialog(item)">
              Open
            </v-btn>
          </v-card-actions>
        </v-card>
        </v-col>
      </v-row>

      <!-- Dialog for Pie Chart -->
      <v-dialog v-model="active" max-width="500px">
        <v-card>
          <v-card-title>
            <h4 class="not-margin">Chart for <b>{{ dialogTitle }}</b></h4>
          </v-card-title>
          <v-card-text>
            <apexchart type="pie" :options="chartOptions" :series="chartSeries"></apexchart>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red" width="200px" dark rounded @click="active = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bar Chart for Overall Summary -->
      <v-row justify="center" class="mt-8">
        <v-col cols="12" md="8">
          <v-card class="mx-auto modern-card" max-width="100%">
            <v-card-title>
              <h3 class="text-h5 font-weight-bold">Overall Summary</h3>
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" :options="barChartOptions" :series="barChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { firebaseApp } from "@/firebaseConfig";
import VueApexCharts from 'vue-apexcharts';

export default {
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      active: false,
      dialogTitle: '',
      chartOptions: {
        chart: {
          width: '100%',
          type: 'pie',
        },
        labels: [],
        colors: ['#ff4560', '#00e396', '#008ffb', '#feb019'],
      },
      chartSeries: [],
      reportItems: [],
      barChartOptions: {
        chart: {
          type: 'bar',
          height: 350,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ['Jobs', 'Returns', 'Purchase Orders', 'Deliveries'],
        },
        yaxis: {
          title: {
            text: 'Count'
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val
            }
          }
        }
      },
      barChartSeries: []
    };
  },
  async created() {
    await this.fetchReportData();
  },
  methods: {
    async fetchReportData() {
      const db = getFirestore(firebaseApp);

      const jobsSnapshot = await getDocs(collection(db, "jobs"));
      const returnsSnapshot = await getDocs(collection(db, "returns"));
      const purchaseOrdersSnapshot = await getDocs(collection(db, "purchaseOrders"));
      const deliveriesSnapshot = await getDocs(collection(db, "deliveries"));

      const completedJobs = jobsSnapshot.docs.filter(doc => doc.data().status === 'Completed').length;
      const pendingJobs = jobsSnapshot.docs.filter(doc => doc.data().status === 'Pending').length;

      const approvedReturns = returnsSnapshot.docs.filter(doc => doc.data().status === 'Approved').length;
      const pendingReturns = returnsSnapshot.docs.filter(doc => doc.data().status === 'Pending').length;

      const approvedPOs = purchaseOrdersSnapshot.docs.filter(doc => doc.data().status === 'Approved').length;
      const rejectedPOs = purchaseOrdersSnapshot.docs.filter(doc => doc.data().status === 'Rejected').length;

      const deliveredItems = deliveriesSnapshot.docs.filter(doc => doc.data().status === 'Delivered').length;
      const inProgressItems = deliveriesSnapshot.docs.filter(doc => doc.data().status === 'In Progress').length;

      this.reportItems = [
        {
          title: 'Jobs',
          img: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          statsInfo: [
            { label: 'Completed', value: completedJobs },
            { label: 'Pending', value: pendingJobs },
          ],
        },
        {
          title: 'Returns',
          img: 'https://plus.unsplash.com/premium_photo-1678567671419-c799c782ac9c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          statsInfo: [
            { label: 'Approved', value: approvedReturns },
            { label: 'Pending', value: pendingReturns },
          ],
        },
        {
          title: 'Purchase Orders',
          img: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          statsInfo: [
            { label: 'Approved', value: approvedPOs },
            { label: 'Rejected', value: rejectedPOs },
          ],
        },
        {
          title: 'Deliveries',
          img: 'https://plus.unsplash.com/premium_photo-1661342486992-2a08d4b466ef?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          statsInfo: [
            { label: 'Delivered', value: deliveredItems },
            { label: 'In Progress', value: inProgressItems },
          ],
        },
      ];

      this.barChartSeries = [{
        name: 'Count',
        data: [completedJobs + pendingJobs, approvedReturns + pendingReturns, approvedPOs + rejectedPOs, deliveredItems + inProgressItems]
      }];
    },
    showDialog(item) {
      this.dialogTitle = item.title;
      // Set chart options and series based on the item data
      this.chartOptions.labels = item.statsInfo.map((stat) => stat.label);
      this.chartSeries = item.statsInfo.map((stat) => stat.value);
      this.active = true;
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

body, div, span, v-card-title, v-card-subtitle, v-card-text, v-row, v-col, v-card {
  font-family: 'Montserrat', sans-serif;
}

.modern-card {
  background-color: white;
  color: black;
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modern-card-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  position: relative;
}

.modern-card-number {
  background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent background */
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 8px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.modern-card-details {
  padding: 16px;
  text-align: center;
}

.v-btn {
  font-size: 0.9rem;
  text-transform: none;
}

.v-card-actions {
  padding: 0 16px 16px;
  display: flex;
  justify-content: center;
}
</style>
